export interface HttpOptions extends RequestInit { json?: any; auth?: boolean; }

async function getTokens(): Promise<{ apiToken: string | null; graphToken: string | null }> {
    try {
        const mod = await import('../auth/msal');
        if (typeof mod.getApiAndGraphTokens === 'function') {
            const tokens = await mod.getApiAndGraphTokens();
            // basic debug logging to verify Graph/API token acquisition path
            console.debug('getTokens: using getApiAndGraphTokens, apiToken present:', !!tokens.apiToken, 'graphToken present:', !!tokens.graphToken);
            return tokens;
        }
        // Fallback: only API token via getAccessTokenSilent
        const apiToken = await mod.getAccessTokenSilent();
        console.debug('getTokens: fallback to getAccessTokenSilent, apiToken present:', !!apiToken);
        return { apiToken, graphToken: null };
    } catch {
        console.warn('getTokens: failed to load msal or acquire token');
        return { apiToken: null, graphToken: null };
    }
}

async function triggerReauth() {
    try {
        const mod = await import('../auth/msal');
        await mod.ensureReauth(false);
    } catch { /* ignore */ }
}

export async function http<T = any>(url: string, options: HttpOptions = {}): Promise<T> {
    const { json, headers, auth = true, ...rest } = options;
    const init: RequestInit = {
        ...rest,
        headers: { 'Content-Type': 'application/json', ...(headers || {}) }
    };
    if (json !== undefined) init.body = JSON.stringify(json);

    if (auth) {
        const { apiToken, graphToken } = await getTokens();
        if (!apiToken) {
            // Attempt to re-authenticate and do NOT send the request without a token
            await triggerReauth();
            throw new Error('401 Unauthorized');
        }
        (init.headers as any)['Authorization'] = `Bearer ${apiToken}`;
        if (graphToken) {
            // Graph token gaat mee voor backend-Graph-calls
            (init.headers as any)['X-Graph-Access-Token'] = graphToken;
        }
    }

    const res = await fetch(url, init);

    if (auth && res.status === 401) {
        await triggerReauth();
        throw new Error('401 Unauthorized');
    }

    if (res.status === 403) throw new Error('Not authorized. Ask an administrator to add your email address.');
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) return await res.json();
    return await (res.text() as any);
}